import { ThemeEvents, CartAddEvent, CartErrorEvent } from '@theme/events';

class BundleOfferComponent extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  /** @type {boolean} */
  #pending = false;

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    wrapProductFormSubmit();
    this.#bindGroupName();
    this.#ensureSelection();
    this.#syncPressedState();

    this.addEventListener('change', this.#onChange, { signal });
    document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
    document.addEventListener('shopify:section:unload', this.#onSectionUnload, { signal });

    const section = this.closest('.shopify-section');
    section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    this.#pending = false;
  }

  hasExtraItems() {
    const selected = this.#selectedInput();
    if (!selected || selected.disabled) return false;
    return this.#extraItems(selected).length > 0;
  }

  /**
   * @param {HTMLFormElement} form
   * @param {HTMLButtonElement | null} button
   */
  submitSelected(form, button) {
    return this.#addSelected(form, button);
  }

  #bindGroupName() {
    const groupName = `bundle-offer-${this.dataset.blockId || this.id || 'group'}`;
    this.querySelectorAll('.bundle-offer__input').forEach((input) => {
      if (input instanceof HTMLInputElement) input.name = groupName;
    });
  }

  #ensureSelection() {
    const inputs = Array.from(this.querySelectorAll('.bundle-offer__input')).filter(
      (input) => input instanceof HTMLInputElement && !input.disabled
    );
    if (inputs.length === 0) return;

    const checked = inputs.filter((input) => input instanceof HTMLInputElement && input.checked);
    if (checked.length === 1) return;

    inputs.forEach((input, index) => {
      if (!(input instanceof HTMLInputElement)) return;
      input.checked = index === 0;
    });
  }

  /**
   * @returns {HTMLInputElement | null}
   */
  #selectedInput() {
    const selected = this.querySelector('.bundle-offer__input:checked');
    return selected instanceof HTMLInputElement ? selected : null;
  }

  /**
   * @param {Event} event
   */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;
    this.#syncPressedState();
  };

  #syncPressedState() {
    this.querySelectorAll('.bundle-offer__card').forEach((card) => {
      const input = card.querySelector('.bundle-offer__input');
      if (!(input instanceof HTMLInputElement)) return;
      card.setAttribute('data-selected', input.checked ? 'true' : 'false');
    });
  }

  /**
   * @param {Event} event
   */
  #onBlockSelect = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !this.contains(target)) return;

    const item = target.closest('.bundle-offer__item');
    const input = item?.querySelector('.bundle-offer__input');
    if (!(input instanceof HTMLInputElement) || input.disabled) return;

    input.checked = true;
    this.#syncPressedState();
    input.focus();
  };

  /**
   * @param {Event} event
   */
  #onSectionUnload = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.contains(this)) this.disconnectedCallback();
  };

  /**
   * @param {HTMLFormElement} form
   * @returns {number}
   */
  #currentVariantId(form) {
    const section = this.closest('.shopify-section');
    const swatch = section?.querySelector('product-color-swatches input:checked');
    const swatchId = Number(swatch instanceof HTMLInputElement ? swatch.dataset.variantId : 0);
    if (swatchId) return swatchId;

    const pickerChecked = section?.querySelectorAll('variant-picker input[type="radio"]:checked') ?? [];
    let pickerId = 0;
    pickerChecked.forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      const id = Number(input.dataset.variantId);
      if (id) pickerId = id;
    });
    if (pickerId) return pickerId;

    const variantInput = form.querySelector('input[name="id"]');
    return Number(variantInput instanceof HTMLInputElement ? variantInput.value : 0);
  }

  /**
   * @param {HTMLInputElement} input
   * @returns {{ id: number, quantity: number }[]}
   */
  #extraItems(input) {
    return (input.dataset.extraItems || '')
      .split(',')
      .map((part) => {
        const [idRaw, qtyRaw] = part.split(':');
        const id = Number((idRaw || '').trim());
        const quantity = Number((qtyRaw || '1').trim()) || 1;
        return { id, quantity };
      })
      .filter((item) => Number.isFinite(item.id) && item.id > 0 && item.quantity > 0);
  }

  /**
   * @param {HTMLFormElement} form
   * @param {HTMLButtonElement | null} button
   */
  async #addSelected(form, button) {
    if (this.#pending) return;

    const selected = this.#selectedInput();
    const currentId = this.#currentVariantId(form);
    const quantityInput = form.querySelector('input[name="quantity"]');
    const quantity = Number(quantityInput instanceof HTMLInputElement ? quantityInput.value : 1) || 1;

    if (!currentId || !selected) return;

    /** @type {Map<number, number>} */
    const quantities = new Map();
    quantities.set(currentId, quantity);

    for (const extra of this.#extraItems(selected)) {
      quantities.set(extra.id, (quantities.get(extra.id) || 0) + extra.quantity * quantity);
    }

    const items = Array.from(quantities.entries()).map(([id, qty]) => ({ id, quantity: qty }));

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });

    const errorEl = this.querySelector('[data-bundle-error]');
    if (errorEl instanceof HTMLElement) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    this.#pending = true;
    if (button) button.disabled = true;

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();

      if (data.status) {
        const message = data.description || data.message || '';
        if (errorEl instanceof HTMLElement) {
          errorEl.hidden = false;
          errorEl.textContent = message;
        }

        document.dispatchEvent(new CartErrorEvent(form.id, data.message, data.description, data.errors));
        return;
      }

      document.dispatchEvent(
        new CartAddEvent(data, form.id, {
          source: 'product-form-component',
          productId: this.dataset.productId,
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error(error);
    } finally {
      this.#pending = false;
      if (button) button.disabled = false;
    }
  }

  /**
   * @param {Event} event
   */
  #onVariantUpdate = (event) => {
    const detail = /** @type {{ detail?: { data?: { html?: Document, productId?: string, newProduct?: { id: string } } } }} */ (
      event
    ).detail;
    const data = detail?.data;
    if (!data?.html) return;

    if (data.newProduct) {
      this.dataset.productId = data.newProduct.id;
    } else if (data.productId && this.dataset.productId && data.productId !== this.dataset.productId) {
      return;
    }

    const next = data.html.querySelector(`bundle-offer-component[data-block-id="${this.dataset.blockId}"]`);
    if (!next) return;

    this.querySelectorAll('[data-bundle-pricing]').forEach((currentNode) => {
      const key = currentNode.getAttribute('data-bundle-pricing');
      const nextNode = next.querySelector(`[data-bundle-pricing="${key}"]`);
      if (nextNode) currentNode.replaceWith(nextNode.cloneNode(true));
    });
  };
}

function wrapProductFormSubmit() {
  const Ctor = customElements.get('product-form-component');
  if (!Ctor || Ctor.prototype.__bundleOfferPatched) return;

  const original = Ctor.prototype.handleSubmit;

  Ctor.prototype.handleSubmit = function handleSubmitWithBundle(event) {
    const section = this.closest('.shopify-section');
    const bundle = section?.querySelector('bundle-offer-component');

    if (bundle instanceof BundleOfferComponent && bundle.hasExtraItems()) {
      event.preventDefault();
      const form = this.querySelector('form');
      const button = this.querySelector('[ref="addToCartButton"]');
      if (form instanceof HTMLFormElement) {
        bundle.submitSelected(form, button instanceof HTMLButtonElement ? button : null);
      }
      return;
    }

    return original.call(this, event);
  };

  Ctor.prototype.__bundleOfferPatched = true;
}

customElements.whenDefined('product-form-component').then(wrapProductFormSubmit);

if (!customElements.get('bundle-offer-component')) {
  customElements.define('bundle-offer-component', BundleOfferComponent);
}
